/**
 * Customer API keys management operations
 */

import { CustomerApiKey } from './types';
import { mockCustomers } from './base';

// Mock API keys data
const mockApiKeys: Record<string, CustomerApiKey[]> = {
  'customer-uuid-1': [
    {
      id: 'ak1',
      name: 'Production App',
      key: 'fp_prod_1234567890abcdef',
      type: 'personal',
      created_at: '2024-01-15T10:30:00Z',
      last_used: '2024-12-20T09:15:00Z',
      permissions: ['products:read', 'products:write'],
      is_active: true
    },
    {
      id: 'ak2',
      name: 'Team Analytics',
      key: 'fp_team_abcdef1234567890',
      type: 'team',
      created_at: '2024-02-01T14:20:00Z',
      last_used: '2024-12-19T16:30:00Z',
      permissions: ['analytics:read'],
      is_active: true
    }
  ],
  'customer-uuid-2': [
    {
      id: 'ak3',
      name: 'Development Key',
      key: 'fp_trial_xyz789012345',
      type: 'personal',
      created_at: '2024-11-01T09:00:00Z',
      last_used: '2024-12-18T14:22:00Z',
      permissions: ['products:read'],
      is_active: true
    }
  ],
  'customer-uuid-3': [
    {
      id: 'ak4',
      name: 'Production Services',
      key: 'fp_prod_enterprise_abc123',
      type: 'personal',
      created_at: '2023-08-20T14:15:00Z',
      last_used: '2024-12-20T07:45:00Z',
      permissions: ['products:read', 'products:write', 'analytics:read'],
      is_active: true
    },
    {
      id: 'ak5',
      name: 'Team Dashboard',
      key: 'fp_team_global_def456',
      type: 'team',
      created_at: '2024-01-10T11:30:00Z',
      last_used: '2024-12-19T22:15:00Z',
      permissions: ['analytics:read', 'billing:read'],
      is_active: true
    }
  ],
  'customer-uuid-4': [
    {
      id: 'ak6',
      name: 'Mobile App',
      key: 'fp_mobile_innovative123',
      type: 'personal',
      created_at: '2024-03-15T09:30:00Z',
      last_used: '2024-12-19T12:45:00Z',
      permissions: ['products:read'],
      is_active: true
    }
  ],
  'customer-uuid-5': [
    {
      id: 'ak7',
      name: 'Legacy Integration',
      key: 'fp_legacy_datadriven456',
      type: 'personal',
      created_at: '2024-02-05T11:00:00Z',
      last_used: '2024-11-10T14:22:00Z',
      permissions: ['products:read'],
      is_active: false
    }
  ]
};

// Initialize customers with their API keys
mockCustomers.forEach(customer => {
  customer.api_keys = mockApiKeys[customer.id] || [];
});

export const apiKeyOperations = {
  list: (customerId: string): { data: CustomerApiKey[] } => {
    const customer = mockCustomers.find(c => c.id === customerId);
    if (!customer) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Customer not found',
          },
        },
      };
    }
    return { data: customer.api_keys };
  },

  create: (customerId: string, data: { name: string; permissions: string[]; type?: 'personal' | 'team' }): { data: CustomerApiKey } => {
    const customer = mockCustomers.find(c => c.id === customerId);
    if (!customer) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Customer not found',
          },
        },
      };
    }

    const newApiKey: CustomerApiKey = {
      id: `ak${Date.now()}`,
      name: data.name,
      key: `fp_${Math.random().toString(36).substring(2, 15)}`,
      type: data.type || 'personal',
      created_at: new Date().toISOString(),
      permissions: data.permissions,
      is_active: true
    };

    customer.api_keys.push(newApiKey);
    return { data: newApiKey };
  },

  update: (customerId: string, keyId: string, data: { name?: string; permissions?: string[]; is_active?: boolean }): { data: CustomerApiKey } => {
    const customer = mockCustomers.find(c => c.id === customerId);
    if (!customer) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Customer not found',
          },
        },
      };
    }

    const keyIndex = customer.api_keys.findIndex(key => key.id === keyId);
    if (keyIndex === -1) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'API key not found',
          },
        },
      };
    }

    customer.api_keys[keyIndex] = {
      ...customer.api_keys[keyIndex],
      ...data
    };

    return { data: customer.api_keys[keyIndex] };
  },

  delete: (customerId: string, keyId: string): { message: string } => {
    const customer = mockCustomers.find(c => c.id === customerId);
    if (!customer) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Customer not found',
          },
        },
      };
    }

    const keyIndex = customer.api_keys.findIndex(key => key.id === keyId);
    if (keyIndex === -1) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'API key not found',
          },
        },
      };
    }

    customer.api_keys.splice(keyIndex, 1);
    return { message: 'API key deleted successfully' };
  },

  regenerate: (customerId: string, keyId: string): { data: CustomerApiKey } => {
    const customer = mockCustomers.find(c => c.id === customerId);
    if (!customer) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Customer not found',
          },
        },
      };
    }

    const keyIndex = customer.api_keys.findIndex(key => key.id === keyId);
    if (keyIndex === -1) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'API key not found',
          },
        },
      };
    }

    // Generate new key
    customer.api_keys[keyIndex].key = `fp_${Math.random().toString(36).substring(2, 15)}`;
    customer.api_keys[keyIndex].last_used = undefined; // Reset last used

    return { data: customer.api_keys[keyIndex] };
  }
};
